/* hm-trustpilot-reviews — scroll-snap review slider with arrows, dots, autoplay */
(() => {
  if (customElements.get('hm-tpr-slider')) return;
  const mqDesktop = window.matchMedia('(min-width: 750px)');

  class HmTprSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.hm-tpr__track');
      this.slides = Array.from(this.querySelectorAll('.hm-tpr__slide'));
      this.dotsEl = this.querySelector('.hm-tpr__dots');
      this.prev = this.querySelector('.hm-tpr__arrow--prev');
      this.next = this.querySelector('.hm-tpr__arrow--next');
      if (!this.track || !this.slides.length) return;

      this.page = 0;
      this.onPrev = () => this.go(this.page - 1, true);
      this.onNext = () => this.go(this.page + 1, true);
      this.onScroll = () => { clearTimeout(this.scrollT); this.scrollT = setTimeout(() => this.syncFromScroll(), 60); };
      this.onMq = () => this.setup();
      this.onEnter = () => { this.hover = true; };
      this.onLeave = () => { this.hover = false; };

      this.prev && this.prev.addEventListener('click', this.onPrev);
      this.next && this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
      mqDesktop.addEventListener ? mqDesktop.addEventListener('change', this.onMq) : mqDesktop.addListener(this.onMq);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.setup(true));
        this.ro.observe(this.track);
      }
      this.setup();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.ro && this.ro.disconnect();
      mqDesktop.removeEventListener ? mqDesktop.removeEventListener('change', this.onMq) : mqDesktop.removeListener(this.onMq);
    }

    get isActive() {
      return mqDesktop.matches ? this.dataset.desktop === 'true' : this.dataset.mobile === 'true';
    }

    get perPage() {
      return mqDesktop.matches ? Math.max(1, parseInt(this.dataset.colsDesktop, 10) || 1) : 1;
    }

    get perMove() {
      return mqDesktop.matches ? Math.max(1, parseInt(this.dataset.perMoveDesktop, 10) || 1) : 1;
    }

    setup(keepPage) {
      this.stopAutoplay();
      if (!this.isActive) {
        this.pages = 1;
        if (this.dotsEl) this.dotsEl.innerHTML = '';
        return;
      }
      const n = this.slides.length;
      const pp = Math.min(this.perPage, n);
      this.pages = n <= pp ? 1 : Math.ceil((n - pp) / this.perMove) + 1;
      if (!keepPage) this.page = 0;
      this.page = Math.min(this.page, this.pages - 1);
      this.buildDots();
      this.update();
      this.startAutoplay();
    }

    buildDots() {
      if (!this.dotsEl) return;
      if (this.dotsEl.childElementCount === this.pages) return;
      this.dotsEl.innerHTML = '';
      if (this.pages <= 1) return;
      for (let i = 0; i < this.pages; i++) {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'hm-tpr__dot';
        b.setAttribute('aria-label', `Go to slide ${i + 1}`);
        b.addEventListener('click', () => this.go(i, true));
        li.appendChild(b);
        this.dotsEl.appendChild(li);
      }
    }

    slideIndexForPage(p) {
      const n = this.slides.length;
      return Math.max(0, Math.min(p * this.perMove, n - Math.min(this.perPage, n)));
    }

    go(p, user) {
      if (!this.isActive || this.pages <= 1) return;
      const loop = this.dataset.type === 'loop';
      if (p < 0) p = loop ? this.pages - 1 : 0;
      if (p > this.pages - 1) p = loop ? 0 : this.pages - 1;
      this.page = p;
      const slide = this.slides[this.slideIndexForPage(p)];
      const side = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft - side;
      this.track.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      if (window.HM && window.HM.pauseVideosIn) window.HM.pauseVideosIn(this);
      this.update();
      if (user) this.startAutoplay();
    }

    syncFromScroll() {
      if (!this.isActive || this.pages <= 1) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      let p;
      if (this.track.scrollLeft >= max - 2) {
        p = this.pages - 1;
      } else {
        const tr = this.track.getBoundingClientRect();
        const side = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
        let best = 0, bestD = Infinity;
        this.slides.forEach((s, i) => {
          const d = Math.abs(s.getBoundingClientRect().left - tr.left - side);
          if (d < bestD) { bestD = d; best = i; }
        });
        p = Math.round(best / this.perMove);
      }
      if (p !== this.page) { this.page = Math.max(0, Math.min(p, this.pages - 1)); this.update(); }
    }

    update() {
      const loop = this.dataset.type === 'loop';
      if (this.prev) this.prev.disabled = !loop && this.page === 0;
      if (this.next) this.next.disabled = !loop && this.page >= this.pages - 1;
      if (this.dotsEl) Array.from(this.dotsEl.querySelectorAll('.hm-tpr__dot')).forEach((d, i) => {
        d.setAttribute('aria-current', i === this.page ? 'true' : 'false');
      });
      this.classList.toggle('is-overflow', this.pages > 1);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.dataset.autoplay !== 'true' || !this.isActive || this.pages <= 1) return;
      const ms = Math.max(1000, (parseFloat(this.dataset.speed) || 5) * 1000);
      this.timer = setInterval(() => {
        if (this.hover || document.hidden) return;
        const loop = this.dataset.type === 'loop';
        if (!loop && this.page >= this.pages - 1) this.go(0);
        else this.go(this.page + 1);
      }, ms);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('hm-tpr-slider', HmTprSlider);
})();
